<script>
  import { onMount, tick } from 'svelte';
  import { ArrowLeft, Forward, Info, Phone, Search, Trash2, Video, X } from 'lucide-svelte';
  import { get } from 'svelte/store';
  import MessageViewport from '@components/messenger/messages/MessageViewport.svelte';
  import MessageComposer from '@components/messenger/messages/MessageComposer.svelte';
  import { startOutgoingCall } from '@components/messenger/calls/call-store.js';
  import Documents from '@components/messenger/Documents.svelte';
  import MessageSearchPanel from '@components/messenger/searchs/messages/MessageSearchPanel.svelte';
  import ShareMessageToConversation from '@components/messenger/ShareMessageToConversation.svelte';
  import StickerPicker from '@components/messenger/StickerPicker.svelte';
  import ConversationEmptyState from '@components/messenger/sharing/ConversationEmptyState.svelte';
  import {
    sendMessage,
    messages,
    myId,
    conversationId,
    status,
    loadMessagesWS,
    replyingTo,
    selectionMode,
    selectedMessageIds,
    typingUsers
  } from '$lib/websocket-client.js';

  export let activeChat;
  export let back;

  $: selected = $conversationId;
  $: typing = $typingUsers[$conversationId];

  let showInfo = false;
  let showSearch = false;
  let showShare = false;
  let showStickerPicker = false;
  let loadedConversationId = '';

  $: wsReady = String($status || '').includes('Connected');
  function openCall(type) {
    if (!selected) return;
    startOutgoingCall({
      conversationId: selected,
      callType: type,
      title: activeChat?.title || activeChat?.name
    });
  }

  function handleStickerSelect(sticker) {
    sendMessage({
      type: 'chat',
      conversationId: selected,
      from: $myId,
      msgType: 'sticker',
      message: sticker.url,
      meta: { stickerId: sticker.id },
      replyTo: get(replyingTo)?.id || null
    });

    showStickerPicker = false;
    replyingTo.set(null);
  }

  function clearSelection() {
    selectedMessageIds.set(new Set());
    selectionMode.set(false);
  }

  function deleteSelected() {
    Array.from(get(selectedMessageIds)).forEach((id) => sendMessage({ type: 'delete', messageId: id }));
    clearSelection();
  }

  async function syncConversation(conversationId) {
    if (!conversationId) return;

    await loadMessagesWS(conversationId);
  }

  onMount(() => {
    setTimeout(() => {
      if (wsReady && selected && !messages.length) {
        syncConversation(selected);
      }
    }, 500);
  });
</script>

{#if selected && activeChat}
  <div class="mobile-chat">
    <header class="mobile-header">
      <div class="flex items-center gap-2 min-w-0">
        <button class="head-button" aria-label="Quay lại" onclick={back}>
          <ArrowLeft size="20" />
        </button>
        <div class="min-w-0">
          <div class="font-semibold truncate max-w-[11rem]">{activeChat?.title || activeChat?.name}</div>
          <div class="text-[10px] text-slate-500">My Cloud Messenger</div>
        </div>
      </div>

      <div class="flex items-center gap-1">
        <button class="head-button" aria-label="Gọi thoại" title="Gọi thoại" onclick={() => openCall('audio')}>
          <Phone size="17" />
        </button>
        <button class="head-button" aria-label="Gọi video" title="Gọi video" onclick={() => openCall('video')}>
          <Video size="17" />
        </button>
        <button class="head-button" aria-label="Tìm kiếm" title="Tìm kiếm" onclick={() => (showSearch = true)}>
          <Search size="17" />
        </button>
        <button class="head-button" aria-label="Thông tin" title="Thông tin" onclick={() => (showInfo = true)}>
          <Info size="17" />
        </button>
      </div>
    </header>

    <main class="flex-1 min-h-0 relative overflow-hidden">
      <MessageViewport
        items={$messages}
        conversationId={$conversationId}
        myId={$myId}
        {selected}
        {typing}
        interactionMode="mobile"
        onShareMessage={() => (showShare = true)} />
    </main>

    {#if $selectionMode}
      <div class="selection-bar">
        <button class="head-button" aria-label="Huỷ chọn" onclick={clearSelection}><X size="19" /></button>
        <span>{$selectedMessageIds.size} đã chọn</span>
        <div class="flex items-center gap-1">
          <button class="head-button" aria-label="Chia sẻ" onclick={() => (showShare = true)}>
            <Forward size="18" />
          </button>
          <button class="head-button danger" aria-label="Xoá" onclick={deleteSelected}>
            <Trash2 size="18" />
          </button>
        </div>
      </div>
    {/if}

    {#if showStickerPicker}
      <div class="sticker-panel">
        <StickerPicker onSelect={handleStickerSelect} onClose={() => (showStickerPicker = false)} />
      </div>
    {/if}

    <footer class="mobile-composer">
      <MessageComposer disabled={!selected} onShowStickerPicker={() => (showStickerPicker = !showStickerPicker)} />
    </footer>
  </div>

  {#if showInfo}
    <div class="mobile-panel">
      <Documents
        conversationId={selected}
        onLeft={() => {
          showInfo = false;
          back?.();
        }} />
      <button class="panel-close" aria-label="Đóng thông tin" onclick={() => (showInfo = false)}>
        <X size="20" />
      </button>
    </div>
  {/if}

  {#if showSearch}
    <div class="mobile-panel">
      <MessageSearchPanel conversationId={selected} closeOnJump={true} onClose={() => (showSearch = false)} />
    </div>
  {/if}

  {#if showShare}
    <div class="mobile-share-screen">
      <ShareMessageToConversation
        variant="mobile"
        onClose={() => {
          showShare = false;
          clearSelection();
        }} />
    </div>
  {/if}
{:else}
  <div class="mobile-empty">
    <ConversationEmptyState
      title="Chưa có cuộc trò chuyện nào được mở"
      description="Chọn một đoạn chat trong danh sách để xem tin nhắn, gọi điện hoặc xem nội dung đã chia sẻ trên thiết bị di động." />
  </div>
{/if}

<style>
  .mobile-chat {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100dvh;
    overflow: hidden;
    background: #f8fafc;
  }

  .mobile-header {
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 3.8rem;
    padding: max(0.35rem, env(safe-area-inset-top)) 0.6rem 0.35rem;
    border-bottom: 1px solid rgba(226, 232, 240, 0.76);
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
    backdrop-filter: blur(22px);
  }

  .head-button {
    display: grid;
    place-items: center;
    width: 2.3rem;
    height: 2.3rem;
    border-radius: 999px;
    color: #334155;
    background: rgba(148, 163, 184, 0.12);
    transition:
      transform 120ms ease,
      background 120ms ease;
  }

  .head-button:active {
    transform: scale(0.94);
    background: rgba(148, 163, 184, 0.24);
  }

  .head-button.danger {
    color: #dc2626;
  }

  .mobile-composer {
    z-index: 20;
    flex-shrink: 0;
    padding-bottom: env(safe-area-inset-bottom);
    background: rgba(255, 255, 255, 0.88);
    backdrop-filter: blur(22px);
  }

  .mobile-composer:focus-within {
    padding-bottom: 0;
  }

  .selection-bar {
    z-index: 30;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 3.5rem;
    padding: 0.4rem 0.75rem;
    border-top: 1px solid #e2e8f0;
    color: #475569;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(20px);
    font-size: 0.8rem;
  }

  .mobile-empty {
    width: 100%;
    height: 100dvh;
    background: #f8fafc;
  }

  .sticker-panel {
    position: absolute;
    bottom: 7rem;
    left: 0.5rem;
    z-index: 45;
  }

  .mobile-panel,
  .mobile-share-screen {
    position: fixed;
    inset: 0;
    z-index: 100;
  }

  .mobile-panel {
    background: white;
  }

  .panel-close {
    position: fixed;
    top: max(0.65rem, env(safe-area-inset-top));
    right: 0.65rem;
    z-index: 110;
    display: grid;
    place-items: center;
    width: 2.4rem;
    height: 2.4rem;
    border-radius: 999px;
    color: white;
    background: rgba(15, 23, 42, 0.72);
    backdrop-filter: blur(18px);
  }

  .mobile-share-screen {
    z-index: 120;
    background: white;
  }
</style>
